import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonText,
  IonBackButton,
  IonButtons,
  IonToast,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import { ImpactStyle } from '@capacitor/haptics';
import { refreshOutline } from 'ionicons/icons';
import { supabase } from '../services/supabase';
import { unblockUser } from '../services/connectionsService';
import './BlockedUsers.css';

interface BlockedUser {
  id: string;
  blocked_id: string;
  created_at: string;
  profile?: {
    display_name: string;
    username: string;
  };
}

const BlockedUsers: React.FC = () => {
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadBlockedUsers();
  }, []);

  const loadBlockedUsers = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: blockedData, error: blockedError } = await supabase
      .from('blocked_users')
      .select('id, blocked_id, created_at')
      .eq('blocker_id', user.id)
      .order('created_at', { ascending: false });

    if (blockedError) {
      logger.error('Error loading blocked users:', blockedError);
      setBlockedUsers([]);
      setLoading(false);
      return;
    }

    if (!blockedData || blockedData.length === 0) {
      setBlockedUsers([]);
      setLoading(false);
      return;
    }

    const blockedIds = blockedData.map(b => b.blocked_id);
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, display_name, username')
      .in('id', blockedIds);

    if (profilesError) {
      logger.error('Error loading profiles:', profilesError);
      setBlockedUsers([]);
      setLoading(false);
      return;
    }

    const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
    const transformedData = blockedData.map(blocked => ({
      id: blocked.id,
      blocked_id: blocked.blocked_id,
      created_at: blocked.created_at,
      profile: profilesMap.get(blocked.blocked_id) || { display_name: 'Unknown User', username: 'unknown' }
    }));

    setBlockedUsers(transformedData);
    setLoading(false);
  };

  const handleRefresh = async (event: CustomEvent) => {
    await loadBlockedUsers();
    event.detail.complete();
  };

  const handleUnblock = async (userId: string, username: string) => {

    const result = await unblockUser(userId);

    if (result.success) {
      setBlockedUsers(blockedUsers.filter(b => b.blocked_id !== userId));
      setToastMessage(`Unblocked ${username}`);
      setShowToast(true);
    } else {
      setToastMessage('Failed to unblock user');
      setShowToast(true);
      logger.error('Failed to unblock user:', result.error);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/tab3" />
          </IonButtons>
          <IonTitle>Blocked Users</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="blocked-users-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingIcon={refreshOutline}
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          ></IonRefresherContent>
        </IonRefresher>

        <div className="blocked-users-container">
          {loading ? (
            <div className="empty-state">
              <IonText>Loading...</IonText>
            </div>
          ) : blockedUsers.length === 0 ? (
            <div className="empty-state">
              <h2>No Blocked Users</h2>
              <IonText color="medium">
                <p>You haven't blocked anyone yet.</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {blockedUsers.map((blocked) => (
                <IonItem key={blocked.id}>
                  <IonLabel>
                    <h2>{blocked.profile?.display_name || 'Unknown User'}</h2>
                    <p>@{blocked.profile?.username || 'unknown'}</p>
                    <p className="blocked-date">
                      Blocked {new Date(blocked.created_at).toLocaleDateString()}
                    </p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="outline"
                    color="primary"
                    onClick={() => handleUnblock(blocked.blocked_id, blocked.profile?.username || 'user')}
                  >
                    Unblock
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          color="success"
          aria-live="polite"
        />
      </IonContent>
    </IonPage>
  );
};

export default BlockedUsers;